import { useCallback, useEffect, useRef, useState } from "react"

export type TimerStatus = "running" | "paused" | "done"

// What a caller provides to start a timer. Identity/display fields are optional
// so the hook stays usable outside the recipe page.
export interface StartTimerInput {
  seconds: number
  label: string
  // Recipe step this timer was started from, shown as a numbered bubble.
  step?: number
  context?: string
  // Identifies the in-text duration this timer was started from, so its chip
  // can be tinted to match.
  sourceId?: string
  // Recipe this timer belongs to, used to group the panel across recipes.
  recipeId?: number
  recipeTitle?: string
}

export interface Timer extends Omit<StartTimerInput, "seconds"> {
  id: string
  // Rotating index into the timer color palette; matches chip and panel swatch.
  colorIndex: number
  totalSeconds: number
  remainingSeconds: number
  status: TimerStatus
}

interface UseTimersOptions {
  onComplete?: (timer: Timer) => void
}

export interface UseTimersResult {
  timers: Timer[]
  start: (input: StartTimerInput) => void
  pause: (id: string) => void
  resume: (id: string) => void
  cancel: (id: string) => void
}

// Manages any number of concurrent countdown timers from a single ticking loop.
// A timer reaching zero flips to "done" and fires onComplete exactly once.
export function useTimers(options: UseTimersOptions = {}): UseTimersResult {
  const [timers, setTimers] = useState<Timer[]>([])
  const idRef = useRef(0)
  const notifiedRef = useRef<Set<string>>(new Set())

  const onCompleteRef = useRef(options.onComplete)
  useEffect(() => {
    onCompleteRef.current = options.onComplete
  }, [options.onComplete])

  // Single interval decrements every running timer once per second.
  useEffect(() => {
    const interval = setInterval(() => {
      setTimers((prev) =>
        prev.map((timer) => {
          if (timer.status !== "running") return timer
          const remaining = timer.remainingSeconds - 1
          if (remaining <= 0) {
            return { ...timer, remainingSeconds: 0, status: "done" }
          }
          return { ...timer, remainingSeconds: remaining }
        }),
      )
    }, 1000)
    return () => clearInterval(interval)
  }, [])

  // Fire onComplete once per timer as it transitions to "done", decoupled from
  // the tick so a double-invoked updater can't double-notify.
  useEffect(() => {
    for (const timer of timers) {
      if (timer.status === "done" && !notifiedRef.current.has(timer.id)) {
        notifiedRef.current.add(timer.id)
        onCompleteRef.current?.(timer)
      }
    }
  }, [timers])

  const start = useCallback((input: StartTimerInput) => {
    const { seconds, ...rest } = input
    if (!Number.isFinite(seconds) || seconds <= 0) return
    const total = Math.floor(seconds)
    const n = idRef.current++
    setTimers((prev) => [
      ...prev,
      {
        ...rest,
        id: `timer-${n}`,
        colorIndex: n,
        totalSeconds: total,
        remainingSeconds: total,
        status: "running",
      },
    ])
  }, [])

  const pause = useCallback((id: string) => {
    setTimers((prev) =>
      prev.map((timer) =>
        timer.id === id && timer.status === "running"
          ? { ...timer, status: "paused" }
          : timer,
      ),
    )
  }, [])

  const resume = useCallback((id: string) => {
    setTimers((prev) =>
      prev.map((timer) =>
        timer.id === id && timer.status === "paused"
          ? { ...timer, status: "running" }
          : timer,
      ),
    )
  }, [])

  const cancel = useCallback((id: string) => {
    setTimers((prev) => prev.filter((timer) => timer.id !== id))
  }, [])

  return { timers, start, pause, resume, cancel }
}
